import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { ErrorMessage } from "../../styles/OrderFormStyles";

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.hasError 
    ? 'var(--error-color, #dc2626)' 
    : 'var(--border-color, #d1d5db)'};
  background-color: var(--input-bg);
  color: var(--text-color);
  border-radius: 0.375rem;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color, #4f46e5);
    box-shadow: 0 0 0 1px var(--primary-color, #4f46e5);
  }
  
  &::placeholder {
    color: var(--placeholder-color, #9ca3af);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Handle both themes */
  @media (prefers-color-scheme: dark) {
    border-color: ${props => props.hasError 
      ? 'var(--error-color, #dc2626)' 
      : 'var(--border-color, #374151)'};
    background-color: var(--input-bg, #1f2937);
    color: var(--text-color, #f3f4f6);
  }
`;

const DropdownIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary, #6b7280);
  pointer-events: none;

  @media (prefers-color-scheme: dark) {
    color: var(--text-secondary, #9ca3af);
  }
`;

const DropdownList = styled.ul`
  position: absolute;
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  margin-top: 4px;
  padding: 0;
  background-color: var(--dropdown-bg, #ffffff);
  border: 1px solid var(--border-color, #d1d5db);
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 10;
  list-style: none;

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: var(--scrollbar-thumb, #cbd5e1);
    border-radius: 3px;
  }

  @media (prefers-color-scheme: dark) {
    background-color: var(--dropdown-bg, #1f2937);
    border-color: var(--border-color, #374151);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1);
    
    &::-webkit-scrollbar-thumb {
      background-color: var(--scrollbar-thumb, #4b5563);
    }
  }
`;

const DropdownItem = styled.li`
  padding: 0.75rem 1rem;
  cursor: pointer;
  color: var(--text-color, #111827);
  
  &:hover {
    background-color: var(--hover-color, #f3f4f6);
  }
  
  &.selected {
    background-color: rgba(79, 70, 229, 0.1);
    font-weight: 500;
  }

  @media (prefers-color-scheme: dark) {
    color: var(--text-color, #f3f4f6);
    
    &:hover {
      background-color: var(--hover-color, #374151);
    }
  }
`;

const NoResults = styled.div`
  padding: 0.75rem 1rem;
  text-align: center;
  color: var(--text-secondary, #6b7280);
  font-style: italic;

  @media (prefers-color-scheme: dark) {
    color: var(--text-secondary, #9ca3af);
  }
`;

// CSS Variables for theme support
const getThemeVars = () => {
  // This function lets us access any theme variables that might exist in the app
  // These will be used alongside media queries for robust theme support
  return {
    // Light theme defaults
    '--input-bg': 'var(--input-bg, #ffffff)',
    '--text-color': 'var(--text-color, #111827)',
    '--text-secondary': 'var(--text-secondary, #6b7280)',
    '--border-color': 'var(--border-color, #d1d5db)',
    '--dropdown-bg': 'var(--dropdown-bg, #ffffff)',
    '--hover-color': 'var(--hover-color, #f3f4f6)',
    '--placeholder-color': 'var(--placeholder-color, #9ca3af)',
    '--scrollbar-thumb': 'var(--scrollbar-thumb, #cbd5e1)',
    '--primary-color': 'var(--primary-color, #4f46e5)',
    '--error-color': 'var(--error-color, #dc2626)',
  };
};

const SearchableMedicineDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Search for a medicine...", 
  disabled = false,
  error = null,
  name,
  theme = 'auto' // 'auto', 'light', or 'dark'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [displayValue, setDisplayValue] = useState("");
  const containerRef = useRef(null);
  
  // Sort options alphabetically by medicine_name
  const sortedOptions = [...options].sort((a, b) => 
    a.medicine_name.localeCompare(b.medicine_name)
  );
  
  // Filter options based on search term
  const filteredOptions = searchTerm 
    ? sortedOptions.filter(option => 
        option.medicine_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (option.medicine_sku && option.medicine_sku.toLowerCase().includes(searchTerm.toLowerCase())))
    : sortedOptions;
    
  // Update display value when the selected value changes
  useEffect(() => {
    if (value) {
      const selectedOption = options.find(option => option.medicine_id.toString() === value.toString());
      setDisplayValue(selectedOption ? 
        `${selectedOption.medicine_name}${selectedOption.medicine_sku ? ` - ${selectedOption.medicine_sku}` : ''}` 
        : "");
    } else {
      setDisplayValue("");
    }
  }, [value, options]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setDisplayValue(e.target.value);
    
    if (!isOpen) {
      setIsOpen(true);
    }
    
    // If search is cleared and dropdown was selected before, deselect
    if (e.target.value === "" && value) {
      onChange({ target: { name, value: "" } });
    }
  };
  
  const handleOptionClick = (option) => {
    onChange({ target: { name, value: option.medicine_id.toString() } });
    setDisplayValue(`${option.medicine_name}${option.medicine_sku ? ` - ${option.medicine_sku}` : ''}`);
    setSearchTerm("");
    setIsOpen(false);
  };
  
  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };
  
  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };
  
  // Determine if we should force a specific theme
  const themeVars = getThemeVars();
  const themeClass = theme !== 'auto' ? `theme-${theme}` : '';
  
  return (
    <DropdownContainer ref={containerRef} className={themeClass} style={themeVars}>
      <InputContainer>
        <SearchInput
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          placeholder={placeholder}
          disabled={disabled}
          hasError={!!error}
        />
        <DropdownIcon>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </DropdownIcon>
      </InputContainer>
      
      {isOpen && (
        <DropdownList>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <DropdownItem
                key={option.medicine_id}
                className={value === option.medicine_id.toString() ? "selected" : ""}
                onClick={() => handleOptionClick(option)}
              >
                {option.medicine_name}{option.medicine_sku ? ` - ${option.medicine_sku}` : ''}
              </DropdownItem>
            ))
          ) : (
            <NoResults>No medicines found</NoResults>
          )}
        </DropdownList>
      )}
    </DropdownContainer>
  );
};

export default SearchableMedicineDropdown;